/**
 * Opaque building footprint masks — transparent pixels stay walkable (iterate-this parity).
 */
import footprintJson from "./data/building-footprint-masks.json";

export const BUILDING_FOOTPRINT_ALPHA_TOLERANCE = 15;

type UvBounds = { u0: number; v0: number; u1: number; v1: number };
type MaskEntry = { b64: string; bounds: UvBounds };
type FootprintJsonFile = {
  dim: number;
  tex: { width: number; height: number };
  alphaThreshold?: number;
  masks: Record<string, MaskEntry>;
};

const data = footprintJson as FootprintJsonFile;
const TEX_W = data.tex.width;
const TEX_H = data.tex.height;
const MASK_DIM = data.dim;
const MASKS = data.masks;
const decodedCache = new Map<string, Uint8Array>();

export type BuildingOpaqueAlphaSample = (u: number, v: number) => number | null;

/** Map manifest ids (e.g. city_services_40) onto iterate-this mask keys. */
export function resolveMaskEntry(assetId: string | undefined | null): MaskEntry | undefined {
  const id = typeof assetId === "string" ? assetId.trim() : "";
  if (!id) return undefined;
  const direct = MASKS[id];
  if (direct?.b64) return direct;
  const prefixed = `building_${id}`;
  if (MASKS[prefixed]?.b64) return MASKS[prefixed];
  const category = id.replace(/_\d+$/, "");
  const catKey = `building_${category}`;
  if (MASKS[catKey]?.b64) return MASKS[catKey];
  return undefined;
}

function footprintLotHit(
  lot: { left: number; top: number; span: number },
  wx: number,
  wy: number
): boolean {
  return (
    wx >= lot.left &&
    wx <= lot.left + lot.span &&
    wy >= lot.top &&
    wy <= lot.top + lot.span
  );
}

function unpackMask50(b64: string): Uint8Array {
  const cached = decodedCache.get(b64);
  if (cached) return cached;
  const binary = atob(b64);
  const buf = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    buf[i] = binary.charCodeAt(i) & 0xff;
  }
  const bits = MASK_DIM * MASK_DIM;
  const out = new Uint8Array(bits);
  for (let i = 0; i < bits; i++) {
    out[i] = (buf[i >> 3]! >> (i & 7)) & 1;
  }
  decodedCache.set(b64, out);
  return out;
}

/** Tight façade band for placeholders when no mask or texture sample is available. */
function fallbackOpaqueHit(
  anchorX: number,
  anchorY: number,
  dispW: number,
  dispH: number,
  wx: number,
  wy: number
): boolean {
  const u0 = 0.28;
  const u1 = 0.72;
  const v0 = 0.35;
  const v1 = 0.92;
  const xs = [u0, u0, u1, u1].map((u) => anchorX + (u - 0.5) * dispW);
  const ys = [v0, v1, v0, v1].map((v) => anchorY + (v - 0.5) * dispH);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return wx >= minX && wx <= maxX && wy >= minY && wy <= maxY;
}

/** Texture-space center from Phaser sprite anchor (origin 0.5, 0.9 typical). */
/** World-space AABB of packed mask bounds (tighter than full sprite bounds). */
export function buildingMaskWorldBounds(
  assetId: string,
  anchorWorldX: number,
  anchorWorldY: number,
  displayWidth: number,
  displayHeight: number
): { left: number; right: number; top: number; bottom: number } | null {
  const entry = resolveMaskEntry(assetId);
  if (!entry?.b64) return null;
  const b = entry.bounds;
  const xs = [b.u0, b.u0, b.u1, b.u1].map((u) => anchorWorldX + (u - 0.5) * displayWidth);
  const ys = [b.v0, b.v1, b.v0, b.v1].map((v) => anchorWorldY + (v - 0.5) * displayHeight);
  return {
    left: Math.min(...xs),
    right: Math.max(...xs),
    top: Math.min(...ys),
    bottom: Math.max(...ys),
  };
}

export function buildingAnchorFromSprite(sprite: {
  x: number;
  y: number;
  displayWidth: number;
  displayHeight: number;
  originY: number;
}): { anchorWorldX: number; anchorWorldY: number } {
  const h = sprite.displayHeight;
  return {
    anchorWorldX: sprite.x,
    anchorWorldY: sprite.y - h * (sprite.originY - 0.5),
  };
}

export function worldPointHitsBuildingOpaqueMask(options: {
  assetId: string;
  anchorWorldX: number;
  anchorWorldY: number;
  displayWidth: number;
  displayHeight: number;
  wx: number;
  wy: number;
  /** Live texture alpha (0–255) in display UV; preferred for city/suburb art. */
  sampleAlpha?: BuildingOpaqueAlphaSample;
  /** When false, use JSON mask / footprint only (movement passability — avoids alpha halos). */
  useTextureAlpha?: boolean;
  alphaThreshold?: number;
  /** 6×6 placement lot — blocks only the footprint when no mask/texture. */
  footprintLot?: { left: number; top: number; span: number };
}): boolean {
  const {
    assetId,
    anchorWorldX,
    anchorWorldY,
    displayWidth,
    displayHeight,
    wx,
    wy,
    sampleAlpha,
    useTextureAlpha = true,
    alphaThreshold = BUILDING_FOOTPRINT_ALPHA_TOLERANCE,
    footprintLot,
  } = options;
  const dispW = displayWidth;
  const dispH = displayHeight;
  const u = (wx - anchorWorldX) / dispW + 0.5;
  const v = (wy - anchorWorldY) / dispH + 0.5;

  if (useTextureAlpha && sampleAlpha && u >= 0 && v >= 0 && u <= 1 && v <= 1) {
    const a = sampleAlpha(u, v);
    if (a !== null) return a >= alphaThreshold;
  }

  const entry = resolveMaskEntry(assetId);
  if (entry?.b64) {
    const mask = unpackMask50(entry.b64);
    let any = false;
    for (let i = 0; i < mask.length; i++) {
      if (mask[i]) {
        any = true;
        break;
      }
    }
    if (any) {
      const b = entry.bounds;
      if (u < b.u0 || u > b.u1 || v < b.v0 || v > b.v1) return false;
      const nu = (u - b.u0) / (b.u1 - b.u0);
      const nv = (v - b.v0) / (b.v1 - b.v0);
      const mx = Math.min(MASK_DIM - 1, Math.max(0, Math.floor(nu * MASK_DIM)));
      const my = Math.min(MASK_DIM - 1, Math.max(0, Math.floor(nv * MASK_DIM)));
      return mask[my * MASK_DIM + mx]! === 1;
    }
  }

  if (footprintLot) return footprintLotHit(footprintLot, wx, wy);

  if (u < 0 || v < 0 || u >= 1 || v >= 1) return false;
  return fallbackOpaqueHit(anchorWorldX, anchorWorldY, dispW, dispH, wx, wy);
}
